import React, { useState, useRef, useEffect, useCallback } from 'react';
import { motion, useInView } from 'framer-motion';
import { ArrowLeft02Icon, ArrowRight02Icon, SparklesIcon } from 'hugeicons-react';
import SectionBadge from '../ui/SectionBadge';

/**
 * Premium Before/After Gallery Section
 * Features:
 * - "Scan Line" automated preview
 * - Glassmorphic Handle
 * - Smooth Drag Interaction (Optimized for Mobile)
 */

// Sample data with distinct "dirty" vs "clean" imagery placeholders
// Note: In production, ensure 'before' and 'after' conform to the same aspect ratio.
const beforeAfterData = [
  {
    id: 1,
    before: 'https://images.unsplash.com/photo-1601362840469-51e4d8d58785?q=80&w=1000&auto=format&fit=crop', // Dirty/Matte car close up
    after: 'https://images.unsplash.com/photo-1542362567-b07e54358753?q=80&w=1000&auto=format&fit=crop', // Shiny/Clean car close up
    title: 'تلميع شامل للسيارة',
    description: 'تحويل كامل من حالة باهتة إلى لمعان المرآة',
    service: 'تلميع ساطع'
  },
  {
    id: 2,
    before: 'https://images.unsplash.com/photo-1607850552763-7da9f77f9888?q=80&w=1000&auto=format&fit=crop', // Interior messy
    after: 'https://images.unsplash.com/photo-1563720223185-11003d516935?q=80&w=1000&auto=format&fit=crop', // Interior clean
    title: 'تنظيف داخلي عميق',
    description: 'إزالة البقع المستعصية وإعادة رونق المقصورة',
    service: 'تنظيف داخلي'
  },
];

export default function BeforeAfterGallery() {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [sliderPosition, setSliderPosition] = useState(50);
  const [isDragging, setIsDragging] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const rafRef = useRef<number | null>(null);
  const pendingPosition = useRef<number | null>(null);
  
  // Auto-scan animation control
  const hasScanned = useRef(false);
  const inViewRef = useRef(null);
  const isInView = useInView(inViewRef, { once: true, margin: "-20%" });

  const currentItem = beforeAfterData[selectedIndex];

  // Automated Scan Effect when scrolled into view
  useEffect(() => {
    if (isInView && !hasScanned.current) {
      hasScanned.current = true;
      const sequence = async () => {
        // Wait a bit
        await new Promise(r => setTimeout(r, 500));
        // Animate slider from 0 to 100 then back to 50
        const duration = 2000;
        const start = Date.now();
        
        const animate = () => {
          const now = Date.now();
          const progress = Math.min((now - start) / duration, 1);
          
          // Easing function for smooth scan
          const ease = (t: number) => t <.5 ? 2*t*t : -1+(4-2*t)*t; 
          
          if (progress < 0.5) {
             // 0 -> 100
             setSliderPosition(ease(progress * 2) * 100);
          } else {
             // 100 -> 50
             const backProgress = (progress - 0.5) * 2;
             setSliderPosition(100 - (ease(backProgress) * 50));
          }

          if (progress < 1) {
            requestAnimationFrame(animate);
          }
        };
        requestAnimationFrame(animate);
      };
      sequence();
    }
  }, [isInView]);

  // Smooth position update using requestAnimationFrame for 60fps
  const updatePosition = useCallback(() => {
    if (pendingPosition.current !== null) {
      setSliderPosition(pendingPosition.current);
      pendingPosition.current = null;
    }
    rafRef.current = null;
  }, []);

  const handleMove = useCallback((clientX: number) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const x = clientX - rect.left;
    const percentage = Math.max(0, Math.min(100, (x / rect.width) * 100));
    
    // Use RAF for smoother updates
    pendingPosition.current = percentage;
    if (!rafRef.current) {
      rafRef.current = requestAnimationFrame(updatePosition);
    }
  }, [updatePosition]);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setIsDragging(true);
    handleMove(e.clientX);
  }, [handleMove]);

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    // Don't prevent default here to allow initial touch to register
    setIsDragging(true);
    handleMove(e.touches[0].clientX);
  }, [handleMove]);

  useEffect(() => {
    const handleUp = () => {
      setIsDragging(false);
      // Cancel any pending RAF
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };
    
    const handleMouseMove = (e: MouseEvent) => {
      if (isDragging) {
        e.preventDefault();
        handleMove(e.clientX);
      }
    };
    
    const handleTouchMove = (e: TouchEvent) => {
      if (isDragging) {
        // Prevent scrolling while dragging for smooth experience
        e.preventDefault();
        handleMove(e.touches[0].clientX);
      }
    };

    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove, { passive: false });
      window.addEventListener('mouseup', handleUp);
      // Use passive: false to allow preventDefault on touch events
      window.addEventListener('touchmove', handleTouchMove, { passive: false });
      window.addEventListener('touchend', handleUp);
      window.addEventListener('touchcancel', handleUp);
      document.body.style.cursor = 'ew-resize';
      // Prevent body scroll on mobile while dragging
      document.body.style.overflow = 'hidden';
    }

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleUp);
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('touchend', handleUp);
      window.removeEventListener('touchcancel', handleUp);
      document.body.style.cursor = 'default';
      document.body.style.overflow = '';
    };
  }, [isDragging, handleMove]);

  // Cleanup RAF on unmount
  useEffect(() => {
    return () => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }
    };
  }, []);

  const handleNext = () => {
    setSelectedIndex((prev) => (prev + 1) % beforeAfterData.length);
    setSliderPosition(50);
  };

  const handlePrev = () => {
    setSelectedIndex((prev) => (prev - 1 + beforeAfterData.length) % beforeAfterData.length);
    setSliderPosition(50);
  };

  return (
    <section className="relative py-24 bg-[#050000] overflow-hidden" ref={inViewRef}>
      {/* Top Gradient Mask */}
      <div className="absolute top-0 left-0 right-0 h-32 bg-gradient-to-b from-[#050000] to-transparent z-10 pointer-events-none" />
      {/* Bottom Gradient Mask */}
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-[#050000] to-transparent z-10 pointer-events-none" />

      {/* Unified background / glow - Intensified */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_rgba(255,0,0,0.15),_transparent_60%)] pointer-events-none" />
      <div className="absolute inset-0 bg-gradient-to-b from-red-600/10 via-transparent to-transparent pointer-events-none" />
      <div className="absolute inset-0 bg-[url('/noise.png')] opacity-[0.06] mix-blend-soft-light pointer-events-none" />

      <div className="container mx-auto px-4 relative z-10">
        <motion.div
           initial={{ opacity: 0, y: 30 }}
           whileInView={{ opacity: 1, y: 0 }}
           viewport={{ once: true }}
           className="text-center mb-16"
        >
          <SectionBadge text="معرض التحولات" icon={SparklesIcon} />
          <h2 className="text-4xl md:text-5xl font-black text-white mb-4">
            قبل <span className="text-transparent bg-clip-text bg-gradient-to-tr from-primary to-red-600">وبعد</span>
          </h2>
          <p className="text-gray-400 max-w-2xl mx-auto text-lg">
            شاهد التحول الرقمي الحقيقي الذي يمكننا تحقيقه.
          </p>
        </motion.div>

        {/* The Comparison Visualizer */}
        <div className="max-w-5xl mx-auto">
          <motion.div 
            className="relative rounded-[2.5rem] overflow-hidden shadow-[0_40px_100px_-20px_rgba(0,0,0,0.8)] border border-white/10 select-none bg-black"
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
          >
            <div 
              ref={containerRef}
              className="relative aspect-[4/5] sm:aspect-[16/9] md:aspect-[21/9] cursor-ew-resize group"
              style={{ touchAction: 'none' }}
              onMouseDown={handleMouseDown}
              onTouchStart={handleTouchStart}
            >
              {/* "Before" Image (Background - Dirty) */}
              <div className="absolute inset-0">
                 <img 
                   src={currentItem.before} 
                   alt={`قبل - ${currentItem.title}`}
                   loading="lazy"
                   className="w-full h-full object-cover pointer-events-none scale-105"
                   draggable={false} 
                 />
                 
                 {/* Before Sticker - Dynamic Visibility */}
                 <motion.div 
                   animate={{ 
                     opacity: sliderPosition < 95 ? 1 : 0,
                     x: sliderPosition > 80 ? (sliderPosition - 80) * 1.5 : 0 
                   }}
                   className="absolute top-4 sm:top-6 right-4 sm:right-6 bg-[#0a0a0a]/60 backdrop-blur-md px-4 py-2 rounded-full text-white font-bold text-[10px] sm:text-xs border border-white/10 z-10 flex items-center gap-2.5 shadow-2xl"
                 >
                   <span className="order-1 uppercase">قبل العناية</span>
                   <div className="order-2 flex h-2 w-2 relative">
                     <div className="animate-ping absolute inline-flex h-full w-full rounded-full bg-gray-500 opacity-75"></div>
                     <div className="relative inline-flex rounded-full h-2 w-2 bg-gray-500"></div>
                   </div>
                 </motion.div>
              </div>

              {/* "After" Image (Foreground - Clean) */}
              <div 
                className="absolute inset-0 overflow-hidden"
                style={{ 
                  clipPath: `inset(0 ${100 - sliderPosition}% 0 0)`,
                  willChange: 'clip-path'
                }}
              >
                <img 
                  src={currentItem.after} 
                  alt={`بعد - ${currentItem.title}`} 
                  loading="lazy"
                  className="w-full h-full object-cover pointer-events-none scale-105"
                  draggable={false}
                />

                 {/* After Sticker - Inside clipped container - Dynamic Visibility */}
                 <motion.div 
                   animate={{ 
                     opacity: sliderPosition > 5 ? 1 : 0,
                     x: sliderPosition < 20 ? (sliderPosition - 20) * 1.5 : 0
                   }}
                   className="absolute top-4 sm:top-6 left-4 sm:left-6 bg-[#1a0000]/60 backdrop-blur-md px-4 py-2 rounded-full text-white font-bold text-[10px] sm:text-xs border border-primary/20 z-10 flex items-center gap-2.5 shadow-[0_0_20px_rgba(220,20,60,0.2)]"
                 >
                   <span className="order-1 uppercase">النتيجة النهائية</span>
                   <div className="order-2 flex h-2 w-2 relative">
                     <div className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75"></div>
                     <div className="relative inline-flex rounded-full h-2 w-2 bg-primary"></div>
                   </div>
                 </motion.div>
              </div>

              {/* Dynamic Magma Glow (follows slider) */}
              <div 
                className="absolute inset-0 pointer-events-none z-10 opacity-40 sm:opacity-60 transition-opacity duration-300"
                style={{ 
                  background: `radial-gradient(circle at ${sliderPosition}% 50%, rgba(220, 20, 60, 0.25) 0%, transparent 40%)`
                }}
              />

              {/* Overlay Gradient for Text Visibility */}
              <div className="absolute inset-x-0 bottom-0 h-2/3 bg-gradient-to-t from-black via-black/40 to-transparent pointer-events-none z-30" />

              {/* Slider Handle */}
              <div 
                className="absolute top-0 bottom-0 w-1 bg-white/10 backdrop-blur-md z-40"
                style={{ 
                  left: `${sliderPosition}%`,
                  willChange: 'left',
                  transform: 'translateZ(0)' // Force GPU acceleration
                }}
              >
                <div className="absolute top-0 bottom-0 -left-[1px] w-[3px] bg-primary shadow-[0_0_30px_rgba(220,20,60,1)]" />
                
                {/* Grab Handle - Enlarged for mobile touch */}
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-14 h-14 rounded-full border border-white/20 bg-black/40 backdrop-blur-2xl shadow-[0_0_50px_rgba(0,0,0,1)] flex items-center justify-center group-hover:scale-110 transition-transform duration-500">
                  <div className="absolute inset-0 rounded-full bg-gradient-to-tr from-primary/20 via-transparent to-white/10 opacity-50" />
                  <div className="relative flex gap-1.5 translate-y-[1px]">
                    <ArrowRight02Icon size={18} className="text-white" />
                    <ArrowLeft02Icon size={18} className="text-white" />
                  </div>
                </div>

                {/* Interaction Hint (Mobile Only) */}
                <motion.div 
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: [0, 1, 0], y: [10, 0, 10] }}
                  transition={{ duration: 3, repeat: Infinity }}
                  className="sm:hidden absolute top-[65%] left-1/2 -translate-x-1/2 whitespace-nowrap bg-black/80 backdrop-blur-md border border-white/10 px-4 py-1.5 rounded-full text-[10px] font-black text-white/60 tracking-widest pointer-events-none"
                >
                  اسحب للمقارنة
                </motion.div>
              </div>

              {/* Info Content - Repositioned for mobile */}
              <div className="absolute bottom-6 sm:bottom-10 right-6 sm:right-10 z-40 text-right pointer-events-none">
                <motion.div
                  key={currentItem.id} // Re-animate on change
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="space-y-1 sm:space-y-3"
                >
                  <div className="inline-flex items-center gap-2 mb-1 px-3 py-1 bg-primary/10 backdrop-blur-md border border-primary/20 rounded-full">
                    <span className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse" />
                    <span className="text-primary font-black text-[9px] sm:text-xs tracking-[0.3em] uppercase">
                      {currentItem.service}
                    </span>
                  </div>
                  <h3 className="text-3xl sm:text-5xl font-black text-white leading-[1.1] tracking-tight">
                    {currentItem.title}
                  </h3>
                  <p className="text-gray-400 text-xs sm:text-base max-w-xs sm:max-w-md hidden sm:block font-medium">
                    {currentItem.description}
                  </p>
                </motion.div>
              </div>

              {/* Desktop Controls - Hidden on very small mobile */}
              <div className="absolute bottom-6 sm:bottom-10 left-6 sm:left-10 z-50 hidden sm:flex gap-4">
                <button 
                  onClick={handlePrev}
                  className="w-12 h-12 sm:w-16 sm:h-16 rounded-2xl bg-white/5 hover:bg-white/10 backdrop-blur-2xl border border-white/10 flex items-center justify-center text-white transition-all hover:scale-105 active:scale-95 group"
                >
                  <ArrowRight02Icon size={28} className="group-hover:translate-x-0.5 transition-transform" />
                </button>
                <button 
                  onClick={handleNext}
                  className="w-12 h-12 sm:w-16 sm:h-16 rounded-2xl bg-primary hover:bg-primary/90 text-white shadow-[0_20px_40px_rgba(220,20,60,0.3)] flex items-center justify-center transition-all hover:scale-105 active:scale-95 group"
                >
                  <ArrowLeft02Icon size={28} className="group-hover:-translate-x-0.5 transition-transform" />
                </button>
              </div>
            </div>

            {/* Mobile-Only Controls - Below the image for better reach */}
            <div className="sm:hidden flex items-center justify-between p-6 bg-white/[0.02] border-t border-white/5">
              <div className="flex gap-4">
                <button 
                  onClick={handlePrev}
                  className="w-12 h-12 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-white active:scale-90 transition-transform"
                >
                  <ArrowRight02Icon size={20} />
                </button>
                <button 
                  onClick={handleNext}
                  className="w-12 h-12 rounded-full bg-primary text-white flex items-center justify-center active:scale-90 transition-transform"
                >
                  <ArrowLeft02Icon size={20} />
                </button>
              </div>
              <div className="text-left">
                <span className="text-gray-500 font-black text-[10px] uppercase tracking-widest">
                  {selectedIndex + 1} / {beforeAfterData.length}
                </span>
              </div>
            </div>
          </motion.div>

          {/* Thumbnails */}
          <div className="flex justify-center gap-4 mt-10">
            {beforeAfterData.map((item, idx) => (
              <button
                key={item.id}
                onClick={() => {
                  setSelectedIndex(idx);
                  setSliderPosition(50);
                }}
                className={`group relative w-20 sm:w-28 h-14 sm:h-20 rounded-2xl overflow-hidden border transition-all duration-500 ${
                  selectedIndex === idx 
                    ? 'border-primary ring-4 ring-primary/20 scale-110 opacity-100 z-10' 
                    : 'border-white/5 opacity-30 hover:opacity-100 hover:border-white/20'
                }`}
              >
                <img
                  src={item.after}
                  alt={`نتيجة بعد - ${item.title}`}
                  loading="lazy"
                  className="w-full h-full object-cover grayscale transition-all duration-700 group-hover:grayscale-0 group-hover:scale-110"
                />
                <div className={`absolute inset-0 bg-primary/20 transition-opacity duration-300 ${selectedIndex === idx ? 'opacity-100' : 'opacity-0'}`} />
              </button>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
